import type { ReactNode } from 'react';

import { cn } from '@shared/ui/cn';

/**
 * One headline figure.
 *
 * The label is an `<h3>` under its section's `<h2>`, so the heading hierarchy
 * has no gap — and the visual treatment is deliberately the opposite of the
 * semantic weight: the label is small secondary sans and the figure is large
 * display, because the figure is what carries the card and a display-face label
 * beside it would compete rather than name.
 *
 * `pending` marks a figure that is a **queue awaiting operator work** — the one
 * thing the action colour is allowed to mean on a reporting surface. Colour is
 * never the only signal: a pending card also carries a `note` line saying what
 * is waiting.
 *
 * There is **no delta half**: no catalog row supplies a comparison period.
 */
export function StatCard({
  label,
  value,
  note,
  pending = false,
  wide = false,
}: {
  label: string;
  /** Already rendered through the shared formatter — Arabic-Indic on a human surface. */
  value: ReactNode;
  note?: string | undefined;
  pending?: boolean | undefined;
  /** Spans the row at the base tier, for a figure whose glyphs do not fit half a phone. */
  wide?: boolean | undefined;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-border bg-surface',
        wide && 'col-span-full md:col-span-1',
      )}
    >
      <div className="p-4">
        <h3 className="font-sans text-xs font-medium leading-meta text-ink-muted">{label}</h3>
        <p
          className={cn(
            'mb-0 mt-2 max-w-none font-display text-h2 font-bold leading-tight [font-variant-numeric:tabular-nums]',
            pending && 'text-accent-text',
          )}
        >
          {value}
        </p>
        {note ? <p className="mb-0 mt-2 max-w-none text-xs text-ink-muted">{note}</p> : null}
      </div>
    </div>
  );
}
